import { arrayMove } from '@dnd-kit/sortable';
import { format } from 'date-fns';
import { CourseStopEntity, StopPayload } from 'src/entities/Course.entity';

/** 便は整数で持ち、表示は「1便」「2便」（ユーザー決定 2026-10-14） */
export const tripLabel = (tripNo: number) => `${tripNo}便`;
export const courseLabel = (course: { code: string; tripNo: number }) => `${course.code} ${tripLabel(course.tripNo)}`;

/**
 * 2 便以降の行がある番号（小文字）。便区分はこの番号の行にだけ出す（log_web_issues#384 13・14）。
 * Why not そのまま比べる: DB の照合順序は a-1 と A-1 を同じ番号として一意性を見る
 */
export const multiTripCodes = (courses: { code: string; tripNo: number }[]) =>
  new Set(courses.filter((c) => c.tripNo >= 2).map((c) => c.code.toLowerCase()));
/** 2 便以降がある番号は「208 1便」、無い番号は「209」 */
export const courseCodeLabel = (course: { code: string; tripNo: number }, multi: Set<string>) =>
  (multi.has(course.code.toLowerCase()) ? courseLabel(course) : course.code);

export const fmtStamp = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'yyyy/MM/dd HH:mm');
};
export const todayString = () => format(new Date(), 'yyyy-MM-dd');

export const toMinutes = (hhmm: string | null | undefined): number | null => {
  const match = hhmm ? /^(\d{2}):(\d{2})$/.exec(hhmm) : null;
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};
export const stayMinutesLabel = (arrival: string | null, departure: string | null) => {
  const from = toMinutes(arrival);
  const to = toMinutes(departure);
  return from == null || to == null || to < from ? '—' : `${to - from}分`;
};

export type TimeOrderRoute = {
  loadingArrivalTime: string | null;
  loadingDepartureTime: string | null;
  stops: { arrivalTime: string | null; departureTime: string | null; excluded: boolean; endedOn: string | null; place: { name: string } }[];
};
/** stopIndex は route.stops の添字（null は積地） */
export type TimeOrderError = { stopIndex: number | null; message: string };

/**
 * 時刻が訪問順に昇順かを見る（L2 の決定。API の Courses::Routes::TimeOrder と同じ規則）。
 * 積地の到着 → 積地の出発 → 1 番目の到着 → 1 番目の出発 → … の順で、空欄は飛ばす。対象外・適用終了の行は比べない。
 * HH:MM はゼロ埋めなので文字列のまま比べる。日跨ぎは昇順でないとして扱う
 */
export function findTimeOrderError(route: TimeOrderRoute): TimeOrderError | null {
  const entries: { stopIndex: number | null; label: string; time: string }[] = [];
  const push = (stopIndex: number | null, label: string, time: string | null) => { if (time) entries.push({ stopIndex, label, time }); };
  push(null, '積地の到着', route.loadingArrivalTime);
  push(null, '積地の出発', route.loadingDepartureTime);
  route.stops.forEach((stop, index) => {
    if (stop.excluded || stop.endedOn) return;
    push(index, `${index + 1} 番目 ${stop.place.name} の到着`, stop.arrivalTime);
    push(index, `${index + 1} 番目 ${stop.place.name} の出発`, stop.departureTime);
  });
  for (let i = 1; i < entries.length; i += 1) {
    const prev = entries[i - 1];
    const cur = entries[i];
    if (cur.time < prev.time) {
      return { stopIndex: cur.stopIndex, message: `時刻が訪問順に並んでいません（${cur.label} ${cur.time} が ${prev.label} ${prev.time} より前です）` };
    }
  }
  return null;
}

/** 絞り込んだ一覧でドラッグしても、見えていない行の順を保つ: 全件の並びの中で active を over の位置へ動かす */
export const reorderIds = (allIds: number[], activeId: number, overId: number): number[] => {
  const from = allIds.indexOf(activeId);
  const to = allIds.indexOf(overId);
  return from < 0 || to < 0 || from === to ? allIds : arrayMove(allIds, from, to);
};

/** API の stops 要素は全状態（送らなかった時刻は NULL になる）。id <= 0 は画面で足した行＝追加 */
export const toStopPayload = (stop: CourseStopEntity, overrides: Partial<StopPayload> = {}): StopPayload => ({
  ...(stop.id > 0 ? { id: stop.id } : {}),
  place_id: stop.place.id,
  arrival_time: stop.arrivalTime,
  departure_time: stop.departureTime,
  driving_duration_seconds: stop.drivingDurationSeconds,
  driving_distance_mm: stop.drivingDistanceMm,
  excluded: stop.excluded,
  ...overrides,
});

/** 便の選択肢: 1〜（既存の最大＋1）。「便追加」のマスタは持たない */
export const nextTripOptions = (courses: { tripNo: number }[]) => {
  const max = courses.reduce((m, c) => Math.max(m, c.tripNo), 1);
  return Array.from({ length: max + 1 }, (_, i) => i + 1);
};

export type IdOption = { id: number; label: string };
/** 今の値（退職・無効になった車両など）が選択肢に無ければ先頭に残す。選び直せないが、開いて保存しただけで消えないように */
export const withCurrentOption = (options: IdOption[], current: IdOption | null): IdOption[] =>
  current && !options.some((o) => o.id === current.id) ? [{ id: current.id, label: `${current.label}（無効）` }, ...options] : options;
